import { useState, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import { Loader2, Heart, Brain, Users, Sparkles, AlertTriangle, CheckCircle, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";

const chartConfig = {
  academic: { label: "تعليمي", color: "hsl(var(--primary))" },
  health: { label: "صحي", color: "hsl(142, 76%, 36%)" },
  psychological: { label: "نفسي", color: "hsl(262, 83%, 58%)" },
  behavioral: { label: "سلوكي", color: "hsl(38, 92%, 50%)" },
};

const CATEGORY_LABELS: Record<string, string> = {
  health: "صحي",
  psychological: "نفسي",
  behavioral: "سلوكي",
};

const SENTIMENT_LABELS: Record<string, { label: string; color: string }> = {
  positive: { label: "إيجابي", color: "text-primary" },
  neutral: { label: "محايد", color: "text-muted-foreground" },
  negative: { label: "سلبي", color: "text-destructive" },
};

interface Props {
  nurseryId: string;
}

const ComprehensiveTracking = ({ nurseryId }: Props) => {
  const [selectedClassId, setSelectedClassId] = useState<string>("all");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedChildId, setSelectedChildId] = useState<string>("");
  const [generating, setGenerating] = useState(false);

  // Fetch classes
  const { data: classes } = useQuery({
    queryKey: ["tracking-classes", nurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("classes")
        .select("id, name")
        .eq("nursery_id", nurseryId)
        .order("name");
      return data || [];
    },
    enabled: !!nurseryId,
  });

  // Fetch children (filtered by class)
  const { data: children } = useQuery({
    queryKey: ["tracking-children", nurseryId, selectedClassId],
    queryFn: async () => {
      let q = supabase
        .from("children")
        .select("id, full_name, class_id")
        .eq("nursery_id", nurseryId)
        .order("full_name");
      if (selectedClassId !== "all") q = q.eq("class_id", selectedClassId);
      const { data } = await q;
      return data || [];
    },
    enabled: !!nurseryId,
  });

  // Fetch weekly reports for all children (aggregate stats)
  const { data: allReports, isLoading: reportsLoading } = useQuery({
    queryKey: ["all-weekly-reports", nurseryId],
    queryFn: async () => {
      const { data } = await supabase
        .from("child_weekly_reports" as any)
        .select("*")
        .eq("nursery_id", nurseryId)
        .order("week_start", { ascending: false });
      return (data || []) as any[];
    },
    enabled: !!nurseryId,
  });

  // Fetch observations (recent, filtered)
  const { data: observations, isLoading: obsLoading } = useQuery({
    queryKey: ["tracking-observations", nurseryId, selectedClassId, selectedCategory],
    queryFn: async () => {
      let q = supabase
        .from("child_observations" as any)
        .select("*, children!child_observations_child_id_fkey(full_name, class_id)")
        .eq("nursery_id", nurseryId)
        .order("observation_date", { ascending: false })
        .limit(50);
      if (selectedCategory !== "all") q = q.eq("category", selectedCategory);
      const { data } = await q;
      let result = (data || []) as any[];
      if (selectedClassId !== "all") {
        result = result.filter((o: any) => o.children?.class_id === selectedClassId);
      }
      return result;
    },
    enabled: !!nurseryId,
  });

  // Fetch weekly reports for selected child (timeline)
  const { data: childReports } = useQuery({
    queryKey: ["child-weekly-reports", selectedChildId],
    queryFn: async () => {
      const { data } = await supabase
        .from("child_weekly_reports" as any)
        .select("*")
        .eq("child_id", selectedChildId)
        .order("week_start", { ascending: true });
      return (data || []) as any[];
    },
    enabled: !!selectedChildId,
  });

  // Aggregate status indicators
  const statusStats = useMemo(() => {
    if (!allReports || allReports.length === 0) return { good: 0, moderate: 0, needs_attention: 0, total: 0 };
    // Get latest report per child
    const latestByChild: Record<string, any> = {};
    allReports.forEach((r: any) => {
      if (!latestByChild[r.child_id] || r.week_start > latestByChild[r.child_id].week_start) {
        latestByChild[r.child_id] = r;
      }
    });
    const reports = Object.values(latestByChild);
    // Filter by class if needed
    const childIds = selectedClassId !== "all" 
      ? (children || []).map((c: any) => c.id)
      : null;
    const filtered = childIds 
      ? reports.filter((r: any) => childIds.includes(r.child_id))
      : reports;
    
    const total = filtered.length;
    const good = filtered.filter((r: any) => r.overall_status === "good").length;
    const moderate = filtered.filter((r: any) => r.overall_status === "moderate").length;
    const needs_attention = filtered.filter((r: any) => r.overall_status === "needs_attention").length;
    return { good, moderate, needs_attention, total };
  }, [allReports, selectedClassId, children]);

  // Timeline data for selected child
  const timelineData = useMemo(() => {
    if (!childReports || childReports.length === 0) return [];
    return childReports.map((r: any) => ({
      week: format(new Date(r.week_start), "d MMM", { locale: ar }),
      academic: r.academic_score,
      health: r.health_score,
      psychological: r.psychological_score,
      behavioral: r.behavioral_score,
    }));
  }, [childReports]);

  const handleGenerateReport = useCallback(async () => {
    if (!selectedChildId) {
      toast.error("اختر طفل أولاً");
      return;
    }
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-child-suggestions", {
        body: { child_id: selectedChildId, nursery_id: nurseryId },
      });
      if (error) throw error;
      toast.success("تم توليد التقرير بنجاح");
    } catch (e: any) {
      toast.error("فشل في توليد التقرير: " + (e.message || "خطأ غير متوقع"));
    } finally {
      setGenerating(false);
    }
  }, [selectedChildId, nurseryId]);

  const isLoading = reportsLoading || obsLoading;

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={selectedClassId} onValueChange={setSelectedClassId}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="الفصل" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">جميع الفصول</SelectItem>
            {(classes || []).map((c: any) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="التصنيف" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">جميع التصنيفات</SelectItem>
            <SelectItem value="health">صحي</SelectItem>
            <SelectItem value="psychological">نفسي</SelectItem>
            <SelectItem value="behavioral">سلوكي</SelectItem>
          </SelectContent>
        </Select>

        <Select value={selectedChildId} onValueChange={setSelectedChildId}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="اختر طفل للرسم البياني..." />
          </SelectTrigger>
          <SelectContent>
            {(children || []).map((c: any) => (
              <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedChildId && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleGenerateReport}
            disabled={generating}
            className="gap-2"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            توليد تقرير ذكي
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Status Indicator Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                label: "حالة جيدة",
                value: statusStats.good,
                percent: statusStats.total > 0 ? Math.round((statusStats.good / statusStats.total) * 100) : 0,
                icon: CheckCircle,
                color: "text-primary",
                bg: "bg-primary/10",
              },
              {
                label: "متوسط",
                value: statusStats.moderate,
                percent: statusStats.total > 0 ? Math.round((statusStats.moderate / statusStats.total) * 100) : 0,
                icon: AlertCircle,
                color: "text-yellow-600",
                bg: "bg-yellow-100 dark:bg-yellow-900/20",
              },
              {
                label: "بحاجة لمتابعة",
                value: statusStats.needs_attention,
                percent: statusStats.total > 0 ? Math.round((statusStats.needs_attention / statusStats.total) * 100) : 0,
                icon: AlertTriangle,
                color: "text-destructive",
                bg: "bg-destructive/10",
              },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Card className="shadow-card">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center shrink-0`}>
                      <s.icon className={`w-5 h-5 ${s.color}`} />
                    </div>
                    <div className="flex-1">
                      <div className="text-xl font-bold text-foreground">{s.value} <span className="text-sm font-normal text-muted-foreground">({s.percent}%)</span></div>
                      <div className="text-xs text-muted-foreground">{s.label}</div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Timeline Chart */}
            {timelineData.length > 1 && (
              <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                <Card className="shadow-card">
                  <CardHeader>
                    <CardTitle className="text-lg">تطور الأبعاد الأربعة عبر الأسابيع</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ChartContainer config={chartConfig} className="h-[300px] w-full">
                      <LineChart data={timelineData}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis dataKey="week" className="text-xs" />
                        <YAxis domain={[0, 5]} />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Line type="monotone" dataKey="academic" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} name="تعليمي" />
                        <Line type="monotone" dataKey="health" stroke="hsl(142, 76%, 36%)" strokeWidth={2} dot={{ r: 3 }} name="صحي" />
                        <Line type="monotone" dataKey="psychological" stroke="hsl(262, 83%, 58%)" strokeWidth={2} dot={{ r: 3 }} name="نفسي" />
                        <Line type="monotone" dataKey="behavioral" stroke="hsl(38, 92%, 50%)" strokeWidth={2} dot={{ r: 3 }} name="سلوكي" />
                      </LineChart>
                    </ChartContainer>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* AI Suggestions for selected child */}
            {selectedChildId && childReports && childReports.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <Card className="shadow-card h-full">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-primary" /> اقتراحات ذكية
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {childReports[childReports.length - 1]?.ai_suggestions ? (
                      <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                        {childReports[childReports.length - 1].ai_suggestions}
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground">لا توجد اقتراحات بعد. اضغط "توليد تقرير ذكي" لإنشاء تقرير.</p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>

          {/* Recent Observations Table */}
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="text-lg">آخر الملاحظات</CardTitle>
              </CardHeader>
              <CardContent>
                {!observations || observations.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground text-sm">لا توجد ملاحظات بعد</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-muted-foreground">
                          <th className="text-right py-2 pr-2 font-medium">الطفل</th>
                          <th className="text-center py-2 font-medium">التصنيف</th>
                          <th className="text-center py-2 font-medium">المشاعر</th>
                          <th className="text-right py-2 font-medium">الوسوم</th>
                          <th className="text-right py-2 font-medium">التاريخ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {observations.map((obs: any) => (
                          <tr key={obs.id} className="border-b last:border-0 hover:bg-muted/30">
                            <td className="py-2.5 pr-2 font-medium">{obs.children?.full_name || "—"}</td>
                            <td className="text-center">
                              <Badge variant="outline" className="text-xs">
                                {CATEGORY_LABELS[obs.category] || obs.category}
                              </Badge>
                            </td>
                            <td className="text-center">
                              <span className={`text-xs font-medium ${SENTIMENT_LABELS[obs.sentiment]?.color || ""}`}>
                                {SENTIMENT_LABELS[obs.sentiment]?.label || obs.sentiment}
                              </span>
                            </td>
                            <td className="text-right">
                              <div className="flex flex-wrap gap-1 justify-end">
                                {(obs.tags || []).slice(0, 3).map((tag: string, i: number) => (
                                  <Badge key={i} variant="secondary" className="text-[10px] px-1.5 py-0">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            </td>
                            <td className="text-right text-muted-foreground text-xs">
                              {obs.observation_date
                                ? format(new Date(obs.observation_date), "d MMM", { locale: ar })
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </>
      )}
    </div>
  );
};

export default ComprehensiveTracking;
